import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import NumberField from './NumberField';
import ResultPanel from './ResultPanel';
import ToolLayout from './ToolLayout';
import { formatNumber, parseNumber } from '../utils/numbers';

function Cylinder() {
  const [cylinderType, setCylinderType] = useState('double');
  const [boreDiameter, setBoreDiameter] = useState('');
  const [rodDiameter, setRodDiameter] = useState('');
  const [stroke, setStroke] = useState('');
  const [pressure, setPressure] = useState('');

  const { result, error } = useMemo(() => {
    const bore = parseNumber(boreDiameter);
    const rod = parseNumber(rodDiameter);
    const strokeNum = parseNumber(stroke);
    const pressureNum = parseNumber(pressure);

    if (bore == null || strokeNum == null || pressureNum == null) {
      return { result: null, error: '' };
    }
    if (cylinderType === 'double' && rod == null) {
      return { result: null, error: 'Rod diameter is required for double-acting cylinders' };
    }

    const boreArea = Math.PI * (bore / 2) ** 2;
    const rodArea = cylinderType === 'double' ? Math.PI * (rod / 2) ** 2 : 0;
    const effectiveArea = boreArea - rodArea;
    const force = pressureNum * effectiveArea * 0.1;
    const volume = boreArea * strokeNum;

    return { result: { force, volume, boreArea, effectiveArea }, error: '' };
  }, [cylinderType, boreDiameter, rodDiameter, stroke, pressure]);

  return (
    <ToolLayout title="Cylinder Force">
      <Card>
        <CardContent>
          <Typography color="text.secondary" paragraph>
            Calculate extend/retract force, bore area, and swept volume from cylinder dimensions and pressure.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl>
                <InputLabel>Cylinder type</InputLabel>
                <Select
                  value={cylinderType}
                  label="Cylinder type"
                  onChange={(event) => setCylinderType(event.target.value)}
                >
                  <MenuItem value="single">Single acting</MenuItem>
                  <MenuItem value="double">Double acting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Bore diameter (mm)" value={boreDiameter} onChange={setBoreDiameter} min={0} />
            </Grid>
            {cylinderType === 'double' && (
              <Grid item xs={12} sm={6}>
                <NumberField label="Rod diameter (mm)" value={rodDiameter} onChange={setRodDiameter} min={0} />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <NumberField label="Stroke (mm)" value={stroke} onChange={setStroke} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Pressure (bar)" value={pressure} onChange={setPressure} min={0} />
            </Grid>
          </Grid>
          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {result && (
            <ResultPanel
              items={[
                { label: 'Force', value: `${formatNumber(result.force, 1)} N` },
                { label: 'Force (kN)', value: `${formatNumber(result.force / 1000, 2)} kN` },
                { label: 'Bore area', value: `${formatNumber(result.boreArea, 1)} mm²` },
                { label: 'Effective area', value: `${formatNumber(result.effectiveArea, 1)} mm²` },
                { label: 'Swept volume', value: `${formatNumber(result.volume, 0)} mm³`, fullWidth: true },
              ]}
            />
          )}
        </CardContent>
      </Card>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Area = π × (diameter / 2)². Force (N) = pressure (bar) × area (mm²) × 0.1, because 1 bar = 0.1 N/mm². Volume = bore area × stroke.
      </Typography>
    </ToolLayout>
  );
}

export default Cylinder;
